/**
 * LoadingSpinner Component
 * 
 * A reusable loading indicator component that displays a spinner animation
 * with optional text. Used throughout the application to indicate loading states.
 * 
 * Requirements: 8.4, 13.1
 */

import './LoadingSpinner.css';

export interface LoadingSpinnerProps {
  /** Size of the spinner: 'small' (24px), 'medium' (48px), 'large' (72px) */
  size?: 'small' | 'medium' | 'large';
  /** Optional text to display below the spinner */
  text?: string;
  /** Whether to center the spinner in its container */
  centered?: boolean;
  /** Additional CSS class name */
  className?: string;
  /** ARIA label for accessibility (defaults to "Loading") */
  ariaLabel?: string;
}

/**
 * LoadingSpinner component that displays an animated loading indicator
 * 
 * @example Basic usage
 * ```tsx
 * <LoadingSpinner />
 * ```
 * 
 * @example With text and custom size
 * ```tsx
 * <LoadingSpinner size="large" text="Loading episodes..." />
 * ```
 * 
 * @example Centered in container
 * ```tsx
 * <LoadingSpinner centered text="Please wait..." />
 * ```
 */
export function LoadingSpinner({
  size = 'medium',
  text,
  centered = false,
  className = '',
  ariaLabel = 'Loading'
}: LoadingSpinnerProps): React.ReactElement {
  const containerClasses = [
    'loading-spinner',
    centered && 'loading-spinner--centered',
    className
  ].filter(Boolean).join(' ');

  const spinnerClasses = [
    'loading-spinner__spinner',
    `loading-spinner__spinner--${size}`
  ].join(' ');

  return (
    <div className={containerClasses} role="status" aria-live="polite">
      <div
        className={spinnerClasses}
        aria-label={ariaLabel}
        aria-busy="true"
      >
        <div className="loading-spinner__circle"></div>
      </div>
      
      {text && (
        <p className="loading-spinner__text">
          {text}
        </p>
      )}
      
      {/* Screen reader only text */}
      <span className="loading-spinner__sr-only">
        {ariaLabel}
      </span>
    </div>
  );
}

export default LoadingSpinner;
